import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useApprovePartner,
  usePartnerRequest,
  useRejectPartner,
  useRevisionPartner,
} from "@/hooks/usePartnerProgram";
import {
  ArrowLeft,
  Check,
  ExternalLink,
  Globe,
  Mail,
  MessageSquare,
  RotateCcw,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useParams } from "wouter";

function StatusBadge({ status }: { status: string }) {
  const variants: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    ACCEPTED: "default",
    REJECTED: "destructive",
    PENDING: "outline",
    UNDER_REVIEW: "secondary",
    NEEDS_REVISION: "outline",
  };

  return (
    <Badge variant={variants[status] || "outline"}>
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

export default function PartnerRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { data: request, isLoading } = usePartnerRequest(id);

  const [dialog, setDialog] = useState<{
    type: "approve" | "reject" | "revision" | null;
  }>({ type: null });
  const [reason, setReason] = useState("");

  const approveMutation = useApprovePartner(() =>
    setLocation("/affiliates/requests"),
  );
  const rejectMutation = useRejectPartner(() =>
    setLocation("/affiliates/requests"),
  );
  const revisionMutation = useRevisionPartner(() =>
    setLocation("/affiliates/requests"),
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 text-center">
        <p>Application not found</p>
        <Button
          variant="ghost"
          onClick={() => setLocation("/affiliates/requests")}
        >
          Go back
        </Button>
      </div>
    );
  }

  const handleConfirmAction = async () => {
    if (!id) return;
    if (dialog.type === "approve") {
      await approveMutation.mutateAsync(id);
    } else if (dialog.type === "reject") {
      await rejectMutation.mutateAsync({ id, reason });
    } else if (dialog.type === "revision") {
      await revisionMutation.mutateAsync({ id, reason });
    }
    setDialog({ type: null });
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/affiliates/requests")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Application Review
            </h1>
            <StatusBadge status={request.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            Submitted on {new Date(request.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Applicant Details</CardTitle>
            <CardDescription>Personal and contact information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase">
                  First Name
                </p>
                <p className="text-sm font-medium">{request.firstName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase">
                  Last Name
                </p>
                <p className="text-sm font-medium">{request.lastName}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase text-slate-500">
                  Email Address
                </p>
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  <p className="text-sm">{request.email}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase text-slate-500">
                  Country
                </p>
                <div className="flex items-center gap-2">
                  <Globe className="h-3 w-3 text-muted-foreground" />
                  <p className="text-sm">{request.country}</p>
                </div>
              </div>
            </div>

            <div className="space-y-1 pt-2 border-t">
              <p className="text-xs font-medium text-muted-foreground uppercase text-slate-500">
                Website / Portfolio
              </p>
              {request.website ? (
                <a
                  href={request.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  {request.website}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No website provided
                </p>
              )}
            </div>

            {request.rejectionReason && (
              <div className="bg-destructive/10 p-4 rounded-lg space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-destructive">
                  <MessageSquare className="h-4 w-4" />
                  Previous Feedback
                </h3>
                <p className="text-sm text-destructive-foreground">
                  {request.rejectionReason}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Review Actions</CardTitle>
              <CardDescription>Decide on this application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                className="w-full justify-start gap-2"
                variant="default"
                onClick={() => setDialog({ type: "approve" })}
              >
                <Check className="h-4 w-4" />
                Approve Partner
              </Button>
              <Button
                className="w-full justify-start gap-2"
                variant="outline"
                onClick={() => setDialog({ type: "revision" })}
              >
                <RotateCcw className="h-4 w-4" />
                Request Revision
              </Button>
              <Button
                className="w-full justify-start gap-2"
                variant="destructive"
                onClick={() => setDialog({ type: "reject" })}
              >
                <X className="h-4 w-4" />
                Reject Application
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {request.user.name || "N/A"}
                  </p>
                  <p className="text-xs text-muted-foreground">Platform User</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={dialog.type !== null}
        onOpenChange={() => setDialog({ type: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog.type === "approve" && "Approve Application"}
              {dialog.type === "reject" && "Reject Application"}
              {dialog.type === "revision" && "Request Revision"}
            </DialogTitle>
            <DialogDescription>
              {dialog.type === "approve" &&
                "This will grant the user partner access and notify them via email."}
              {dialog.type === "reject" &&
                "Please provide a reason for rejection. This will be sent to the user."}
              {dialog.type === "revision" &&
                "Explain what information is missing or needs to be changed."}
            </DialogDescription>
          </DialogHeader>

          {dialog.type !== "approve" && (
            <div className="space-y-2 mt-4">
              <p className="text-sm font-medium">Message to applicant</p>
              <Textarea
                placeholder="Enter your feedback here..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ type: null })}>
              Cancel
            </Button>
            <Button
              variant={dialog.type === "reject" ? "destructive" : "default"}
              onClick={handleConfirmAction}
              disabled={
                approveMutation.isPending ||
                rejectMutation.isPending ||
                revisionMutation.isPending
              }
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
